import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Users, Brain, AlertTriangle, Activity, ArrowRight, Clock, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const URGENCY_COLORS = { emergency: '#ef4444', urgent: '#f97316', routine: '#10b981', follow_up: '#3b82f6' };

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [urgentCases, setUrgentCases] = useState([]);
  const [urgentTotal, setUrgentTotal] = useState(0);
  const [urgentPage, setUrgentPage] = useState(0);
  const [urgentSearch, setUrgentSearch] = useState('');

  const fetchUrgent = async (page = 0, search = '') => {
    try {
      let url = `/analytics/urgent-cases?skip=${page * 10}&limit=10`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await API.get(url);
      setUrgentCases(res.data.cases || []);
      setUrgentTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    Promise.all([
      API.get('/analytics/overview'),
      API.get('/analytics/trends?days=7')
    ]).then(([ov, tr]) => {
      setOverview(ov.data);
      setTrends(tr.data.trends || []);
    }).catch(console.error).finally(() => setLoading(false));
    fetchUrgent(0, '');
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse-subtle text-sky-600"><Activity className="w-8 h-8" /></div>
      </div>
    );
  }

  const pieData = overview?.triage_distribution
    ? Object.entries(overview.triage_distribution).filter(([,v]) => v > 0).map(([name, value]) => ({ name, value }))
    : [];

  const stats = [
    { label: 'Total Patients', value: overview?.total_patients || 0, icon: Users, color: 'text-sky-600', bg: 'bg-sky-50' },
    { label: 'Triage Sessions', value: overview?.total_sessions || 0, icon: Brain, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Active Sessions', value: overview?.active_sessions || 0, icon: Activity, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Urgent Cases', value: (overview?.urgent_cases || 0) + (overview?.emergency_cases || 0), icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
  ];

  return (
    <div className="p-6 lg:p-8 space-y-8 max-w-[1400px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 data-testid="dashboard-title" className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Welcome back, {user?.name}</p>
        </div>
        <Button data-testid="start-triage-btn" onClick={() => navigate('/triage')} className="bg-sky-600 hover:bg-sky-700">
          <Brain className="w-4 h-4 mr-2" /> Start Triage
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
        {stats.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div key={i} className="stat-card animate-fade-in" style={{animationDelay: `${i * 80}ms`}} data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">{stat.label}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1" style={{fontFamily: 'Manrope'}}>{stat.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-xl ${stat.bg} flex items-center justify-center`}>
                  <Icon className={`w-6 h-6 ${stat.color}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Triage Activity (7 days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]" data-testid="trends-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }} />
                  <Bar dataKey="total" fill="#0ea5e9" radius={[4, 4, 0, 0]} name="Total" />
                  <Bar dataKey="completed" fill="#10b981" radius={[4, 4, 0, 0]} name="Completed" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Urgency Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <div className="h-[200px]" data-testid="pie-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                      {pieData.map((entry, i) => (
                        <Cell key={i} fill={URGENCY_COLORS[entry.name] || '#94a3b8'} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '13px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-slate-400 text-sm">No triage data yet</div>
            )}
            <div className="grid grid-cols-2 gap-2 mt-4">
              {Object.entries(URGENCY_COLORS).map(([key, color]) => (
                <div key={key} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                  <span className="text-xs text-slate-600 capitalize">{key.replace('_', ' ')}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-red-200 bg-red-50/30" data-testid="urgent-cases-block">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <CardTitle className="text-lg font-semibold text-red-900" style={{fontFamily: 'Manrope'}}>Urgent & Emergency Cases</CardTitle>
              <Badge className="bg-red-100 text-red-700">{urgentTotal}</Badge>
            </div>
          </div>
          <div className="relative mt-3 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input
              data-testid="urgent-search"
              placeholder="Search by patient name..."
              value={urgentSearch}
              onChange={(e) => { setUrgentSearch(e.target.value); setUrgentPage(0); fetchUrgent(0, e.target.value); }}
              className="pl-9 h-9 text-sm bg-white"
            />
          </div>
        </CardHeader>
        <CardContent>
          {urgentCases.length > 0 ? (
            <>
              <div className="space-y-2" data-testid="urgent-sessions-list">
                {urgentCases.map((session) => (
                  <div key={session.id} className="flex items-center justify-between p-3 rounded-lg bg-white border border-red-100 hover:border-red-300 transition-colors cursor-pointer" onClick={() => navigate(`/triage?session=${session.id}`)}>
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${session.urgency_level === 'emergency' ? 'bg-red-100' : 'bg-orange-100'}`}>
                        <AlertTriangle className={`w-4 h-4 ${session.urgency_level === 'emergency' ? 'text-red-600' : 'text-orange-600'}`} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{session.patient_name || 'Walk-in Patient'}</p>
                        <p className="text-xs text-slate-500 mt-0.5 max-w-md truncate">
                          {session.triage_summary?.summary || session.triage_summary?.recommended_action || ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-right">
                        <span className={`triage-badge ${session.urgency_level === 'emergency' ? 'triage-emergency' : 'triage-urgent'}`}>
                          {session.urgency_level}
                        </span>
                        <p className="text-[10px] text-slate-400 mt-1">{new Date(session.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {urgentTotal > 10 && (
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-red-100">
                  <p className="text-xs text-slate-500">
                    Showing {urgentPage * 10 + 1}-{Math.min((urgentPage + 1) * 10, urgentTotal)} of {urgentTotal}
                  </p>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" disabled={urgentPage === 0}
                      onClick={() => { const p = urgentPage - 1; setUrgentPage(p); fetchUrgent(p, urgentSearch); }} data-testid="urgent-prev">
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-xs text-slate-600 px-2">Page {urgentPage + 1} of {Math.ceil(urgentTotal / 10)}</span>
                    <Button variant="ghost" size="icon" className="h-8 w-8" disabled={(urgentPage + 1) * 10 >= urgentTotal}
                      onClick={() => { const p = urgentPage + 1; setUrgentPage(p); fetchUrgent(p, urgentSearch); }} data-testid="urgent-next">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-6 text-slate-400">
              <AlertTriangle className="w-6 h-6 mx-auto mb-2 opacity-30" />
              <p className="text-sm">{urgentSearch ? 'No matching urgent cases' : 'No urgent or emergency cases'}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Recent Triage Sessions</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate('/triage')} className="text-sky-600 hover:text-sky-700">
            View All <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {overview?.recent_sessions?.length > 0 ? (
            <div className="space-y-3" data-testid="recent-sessions">
              {overview.recent_sessions.slice(0, 5).map((session) => (
                <div key={session.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer" onClick={() => navigate(`/triage?session=${session.id}`)}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-sky-100 flex items-center justify-center">
                      <Brain className="w-4 h-4 text-sky-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{session.patient_name || 'Walk-in Patient'}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Clock className="w-3 h-3 text-slate-400" />
                        <span className="text-xs text-slate-500">{new Date(session.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {session.urgency_level && (
                      <span className={`triage-badge triage-${session.urgency_level}`}>
                        {session.urgency_level.replace('_', ' ')}
                      </span>
                    )}
                    <Badge variant={session.status === 'completed' ? 'default' : 'secondary'} className={session.status === 'completed' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : session.status === 'escalated' ? 'bg-purple-100 text-purple-700' : ''}>
                      {session.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Brain className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No triage sessions yet</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/triage')}>Start First Triage</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
